
import { useState, useRef } from 'react';
import { areasVida, momentosPratica, dicasTonyRobbins } from '../../../mocks/worksheet-encantations';

interface EncantationItem {
  id: number;
  area: string;
  texto: string;
  momento: string;
  movimento: string;
}

const EMPTY_ENCANTATION: Omit<EncantationItem, 'id'> = {
  area: '',
  texto: '',
  momento: '',
  movimento: '',
};

export default function CriadorEncantations() {
  const [encantations, setEncantations] = useState<EncantationItem[]>([
    { id: 1, ...EMPTY_ENCANTATION },
    { id: 2, ...EMPTY_ENCANTATION },
    { id: 3, ...EMPTY_ENCANTATION },
  ]);
  const [activeStep, setActiveStep] = useState(0);
  const [showDica, setShowDica] = useState(false);
  const [dicaIndex, setDicaIndex] = useState(0);
  const [savedToast, setSavedToast] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);

  const addEncantation = () => {
    if (encantations.length >= 8) return;
    setEncantations([
      ...encantations,
      { id: Date.now(), ...EMPTY_ENCANTATION },
    ]);
  };

  const removeEncantation = (id: number) => {
    if (encantations.length <= 1) return;
    setEncantations(encantations.filter((e) => e.id !== id));
  };

  const updateEncantation = (id: number, field: keyof Omit<EncantationItem, 'id'>, value: string) => {
    setEncantations(
      encantations.map((e) => (e.id === id ? { ...e, [field]: value } : e))
    );
  };

  const getAreaInfo = (areaId: string) => areasVida.find((a) => a.id === areaId);

  const handleShowDica = () => {
    setDicaIndex((prev) => (prev + 1) % dicasTonyRobbins.length);
    setShowDica(true);
    setTimeout(() => setShowDica(false), 5000);
  };

  const handleSave = () => {
    const data = {
      encantations,
      dataCriacao: new Date().toISOString(),
    };
    try {
      localStorage.setItem('minhas-encantations', JSON.stringify(data));
      setSavedToast(true);
      setTimeout(() => setSavedToast(false), 3000);
    } catch {
      // silently fail
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const filledCount = encantations.filter((e) => e.texto.trim().length > 0).length;
  const progress = Math.round((filledCount / encantations.length) * 100);

  const steps = [
    { label: 'Escolha a Área', icone: 'ri-compass-3-line' },
    { label: 'Escreva a Encantation', icone: 'ri-edit-line' },
    { label: 'Defina a Prática', icone: 'ri-calendar-check-line' },
  ];

  return (
    <section id="criar-encantations" className="py-24 bg-[#FAF8F5] print:bg-white print:py-8">
      <div className="max-w-5xl mx-auto px-6" ref={formRef}>
        {/* Header */}
        <div className="text-center mb-14 print:mb-8">
          <div className="flex items-center justify-center gap-4 mb-5 print:hidden">
            <div className="w-12 h-px bg-[#D4AF37]/40"></div>
            <span className="font-['Montserrat'] text-xs tracking-[0.3em] uppercase text-[#D4AF37]">
              Sua Vez de Criar
            </span>
            <div className="w-12 h-px bg-[#D4AF37]/40"></div>
          </div>
          <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-bold text-[#1A1A1A] mb-5">
            Suas <span className="text-[#D4AF37] italic">Encantations</span> Personalizadas
          </h2>
          <p className="font-['Montserrat'] text-base text-[#6A6A6A] max-w-2xl mx-auto leading-relaxed print:hidden">
            Preencha cada campo com atenção e intenção. Lembre-se: suas palavras
            têm o poder de transformar sua identidade.
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-10 print:hidden">
          <div className="flex items-center justify-between mb-3">
            <span className="font-['Montserrat'] text-sm text-[#6A6A6A]">
              Progresso: <strong className="text-[#1A1A1A]">{filledCount}/{encantations.length}</strong> Encantations criadas
            </span>
            <span className="font-['Montserrat'] text-sm font-semibold text-[#D4AF37]">{progress}%</span>
          </div>
          <div className="w-full h-2.5 bg-[#D4AF37]/10 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#D4AF37] to-[#B8941E] rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>

        {/* Steps Guide */}
        <div className="flex items-center justify-center gap-2 mb-12 print:hidden">
          {steps.map((step, idx) => (
            <button
              key={idx}
              onClick={() => setActiveStep(idx)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeStep === idx
                  ? 'bg-[#D4AF37] text-black shadow-md'
                  : 'bg-white text-[#6A6A6A] border border-[#D4AF37]/15 hover:border-[#D4AF37]/40'
              }`}
            >
              <i className={`${step.icone} text-base`}></i>
              {step.label}
            </button>
          ))}
        </div>

        {/* Dica Tony Robbins */}
        {showDica && (
          <div className="mb-8 p-5 bg-gradient-to-r from-[#D4AF37]/10 to-[#D4AF37]/5 rounded-xl border border-[#D4AF37]/20 animate-fade-in print:hidden">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                <i className="ri-lightbulb-flash-line text-[#D4AF37] text-lg"></i>
              </div>
              <div>
                <p className="font-['Montserrat'] text-xs text-[#D4AF37] font-semibold uppercase tracking-wider mb-1">
                  Dica de Tony Robbins
                </p>
                <p className="font-['Montserrat'] text-sm text-[#4A4A4A] leading-relaxed italic">
                  &ldquo;{dicasTonyRobbins[dicaIndex]}&rdquo;
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Encantation Cards */}
        <div className="space-y-8">
          {encantations.map((enc, index) => {
            const areaInfo = getAreaInfo(enc.area);
            return (
              <div
                key={enc.id}
                className="bg-white rounded-2xl border border-[#D4AF37]/10 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden print:shadow-none print:border print:border-stone-200"
              >
                {/* Card Header */}
                <div className="flex items-center justify-between px-7 py-4 bg-gradient-to-r from-[#FAF8F5] to-white border-b border-[#D4AF37]/10">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#B8941E] flex items-center justify-center">
                      <span className="font-['Playfair_Display'] text-sm font-bold text-white">
                        {index + 1}
                      </span>
                    </div>
                    <h3 className="font-['Playfair_Display'] text-lg font-bold text-[#1A1A1A]">
                      Encantation #{index + 1}
                    </h3>
                    {enc.texto.trim() && (
                      <div className="w-5 h-5 rounded-full bg-emerald-500/15 flex items-center justify-center">
                        <i className="ri-check-line text-emerald-500 text-xs"></i>
                      </div>
                    )}
                  </div>
                  {encantations.length > 1 && (
                    <button
                      onClick={() => removeEncantation(enc.id)}
                      className="w-8 h-8 rounded-lg text-stone-400 hover:text-red-500 hover:bg-red-50 flex items-center justify-center transition-colors cursor-pointer print:hidden"
                      title="Remover"
                    >
                      <i className="ri-delete-bin-line text-base"></i>
                    </button>
                  )}
                </div>

                {/* Card Body */}
                <div className="p-7 space-y-6">
                  {/* Área da Vida */}
                  <div>
                    <label className="font-['Montserrat'] text-sm font-semibold text-[#1A1A1A] mb-3 block">
                      <i className="ri-compass-3-line text-[#D4AF37] mr-2"></i>
                      Área da Vida
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 print:hidden">
                      {areasVida.map((area) => (
                        <button
                          key={area.id}
                          onClick={() => updateEncantation(enc.id, 'area', area.id)}
                          className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm transition-all cursor-pointer whitespace-nowrap ${
                            enc.area === area.id
                              ? 'bg-[#D4AF37] text-black font-semibold shadow-sm'
                              : 'bg-[#FAF8F5] text-[#6A6A6A] border border-transparent hover:border-[#D4AF37]/20'
                          }`}
                        >
                          <i className={`${area.icone} text-base`}></i>
                          <span className="truncate">{area.label}</span>
                        </button>
                      ))}
                    </div>
                    {enc.area && (
                      <p className="hidden print:block font-['Montserrat'] text-sm text-[#4A4A4A]">
                        {areaInfo?.label}
                      </p>
                    )}

                    {/* Exemplo da área selecionada */}
                    {areaInfo && (
                      <div className="mt-3 p-3 bg-[#D4AF37]/5 rounded-lg border border-[#D4AF37]/10 print:hidden">
                        <p className="font-['Montserrat'] text-xs text-[#8A8A8A] mb-1">
                          <i className="ri-lightbulb-line mr-1"></i>Exemplo para {areaInfo.label}:
                        </p>
                        <p className="font-['Montserrat'] text-sm text-[#D4AF37] italic font-medium">
                          &ldquo;{areaInfo.exemplo}&rdquo;
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Texto da Encantation */}
                  <div>
                    <label className="font-['Montserrat'] text-sm font-semibold text-[#1A1A1A] mb-3 block">
                      <i className="ri-edit-line text-[#D4AF37] mr-2"></i>
                      Sua Encantation
                    </label>
                    <div className="relative">
                      <textarea
                        value={enc.texto}
                        onChange={(e) => {
                          if (e.target.value.length <= 300) {
                            updateEncantation(enc.id, 'texto', e.target.value);
                          }
                        }}
                        placeholder="Comece com &quot;Eu sou...&quot; e escreva com emoção e intensidade. Exemplo: &quot;EU SOU abundância infinita! O sucesso flui para mim com facilidade e alegria!&quot;"
                        className="w-full h-32 px-5 py-4 bg-[#FAF8F5] border-2 border-dashed border-[#D4AF37]/20 rounded-xl text-[#1A1A1A] font-['Montserrat'] text-base leading-relaxed placeholder:text-stone-400 focus:outline-none focus:border-[#D4AF37]/50 focus:bg-white transition-all resize-none print:border-solid print:border-stone-200"
                        maxLength={300}
                      />
                      <div className="absolute bottom-3 right-4 font-['Montserrat'] text-xs text-stone-400 print:hidden">
                        {enc.texto.length}/300
                      </div>
                    </div>
                  </div>

                  {/* Movimento Físico */}
                  <div>
                    <label className="font-['Montserrat'] text-sm font-semibold text-[#1A1A1A] mb-3 block">
                      <i className="ri-run-line text-[#D4AF37] mr-2"></i>
                      Movimento Físico Associado
                    </label>
                    <input
                      type="text"
                      value={enc.movimento}
                      onChange={(e) => updateEncantation(enc.id, 'movimento', e.target.value)}
                      placeholder="Ex: Bater no peito com força, caminhar com passos firmes, cerrar os punhos..."
                      className="w-full px-5 py-3.5 bg-[#FAF8F5] border border-[#D4AF37]/15 rounded-xl text-[#1A1A1A] font-['Montserrat'] text-sm placeholder:text-stone-400 focus:outline-none focus:border-[#D4AF37]/40 focus:bg-white transition-all print:border-stone-200"
                    />
                  </div>

                  {/* Momento de Prática */}
                  <div>
                    <label className="font-['Montserrat'] text-sm font-semibold text-[#1A1A1A] mb-3 block">
                      <i className="ri-time-line text-[#D4AF37] mr-2"></i>
                      Melhor Momento para Praticar
                    </label>
                    <div className="flex flex-wrap gap-2 print:hidden">
                      {momentosPratica.map((m) => (
                        <button
                          key={m.id}
                          onClick={() => updateEncantation(enc.id, 'momento', m.id)}
                          className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all cursor-pointer whitespace-nowrap ${
                            enc.momento === m.id
                              ? 'bg-[#D4AF37] text-black font-semibold'
                              : 'bg-white text-[#6A6A6A] border border-[#D4AF37]/15 hover:border-[#D4AF37]/30'
                          }`}
                        >
                          <i className={`${m.icone} text-sm`}></i>
                          {m.label}
                        </button>
                      ))}
                    </div>
                    {enc.momento && (
                      <p className="hidden print:block font-['Montserrat'] text-sm text-[#4A4A4A]">
                        {momentosPratica.find((m) => m.id === enc.momento)?.label}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Add Button */}
        {encantations.length < 8 && (
          <button
            onClick={addEncantation}
            className="w-full mt-6 py-4 border-2 border-dashed border-[#D4AF37]/25 rounded-2xl text-[#D4AF37] font-['Montserrat'] font-semibold text-sm hover:border-[#D4AF37]/50 hover:bg-[#D4AF37]/5 transition-all cursor-pointer flex items-center justify-center gap-2 print:hidden"
          >
            <i className="ri-add-circle-line text-lg"></i>
            Adicionar Nova Encantation ({encantations.length}/8)
          </button>
        )}

        {/* Action Buttons */}
        <div className="flex flex-wrap items-center justify-center gap-4 mt-10 print:hidden">
          <button
            onClick={handleSave}
            className="px-8 py-3.5 bg-[#D4AF37] text-black font-semibold rounded-lg hover:bg-[#C5A028] transition-all shadow-md hover:shadow-lg flex items-center gap-2 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-save-line text-lg"></i>
            Salvar Minhas Encantations
          </button>
          <button
            onClick={handlePrint}
            className="px-8 py-3.5 bg-white text-[#1A1A1A] font-semibold rounded-lg border border-[#D4AF37]/20 hover:border-[#D4AF37]/40 transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-printer-line text-lg"></i>
            Imprimir Worksheet
          </button>
          <button
            onClick={handleShowDica}
            className="px-8 py-3.5 bg-white text-[#D4AF37] font-semibold rounded-lg border border-[#D4AF37]/20 hover:border-[#D4AF37]/40 transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-lightbulb-flash-line text-lg"></i>
            Dica do Tony Robbins
          </button>
        </div>
      </div>

      {/* Save Toast */}
      {savedToast && (
        <div className="fixed bottom-8 right-8 z-50 bg-emerald-600 text-white px-6 py-4 rounded-xl shadow-2xl flex items-center gap-3 animate-slide-up print:hidden">
          <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
            <i className="ri-check-line text-lg"></i>
          </div>
          <div>
            <p className="font-['Montserrat'] text-sm font-semibold">Salvo com sucesso!</p>
            <p className="font-['Montserrat'] text-xs text-white/80">Suas Encantations foram salvas localmente.</p>
          </div>
        </div>
      )}
    </section>
  );
}
